'use client';

import React from 'react';
import { CustomImage } from 'components/shared';
import { TEST_PANEL_PLANS } from './constants';
import Popover from './Popover';
import { Panel } from './types';
import { P } from '../StyleConstants';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { CTAButtons } from './CTAButtons';
import MobileBiomarkerTable from './MobileBiomarkerTable';
import styles from './BiomarkerTable.module.css';

const cx = (...classes: (string | undefined | false)[]) =>
  classes.filter(Boolean).join(' ');

export function BiomarkerTable({ isModal }: { isModal: boolean }) {
  const effectivePlans = TEST_PANEL_PLANS;
  const left = effectivePlans[0];
  const right = effectivePlans[1];

  const isMobile = useWindowSize().width < 768;

  const maxRows = Math.max(left.panels?.length || 0, right.panels?.length || 0);

  const rows: Array<{ left?: Panel; right?: Panel }> = Array.from(
    { length: maxRows },
    (_, i) => ({
      left: left.panels?.[i],
      right: right.panels?.[i],
    }),
  );

  return (
    <div className={styles.wrapper}>
      {!isModal && <CTAButtons />}
      {/* both rendered, CSS-toggled: useWindowSize() is 0 on SSR */}
      <div className={styles.mobileTableOnly}>
        <MobileBiomarkerTable />
      </div>
      <table className={styles.desktopTableOnly}>
        <thead>
          <tr>
            <th>
              <p className={styles.headerText}>{left.panelHeader}</p>
            </th>
            <th>
              <p className={styles.headerText}>{right.panelHeader}</p>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx}>
              <td>
                {row.left ? (
                  <RowCell item={row.left} isMobile={isMobile} />
                ) : null}
              </td>
              <td>
                {row.right ? (
                  <RowCell item={row.right} isMobile={isMobile} />
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RowCell({ item, isMobile }: { item: Panel; isMobile: boolean }) {
  const [hoverOpen, setHoverOpen] = React.useState(false);
  const iconRef = React.useRef<HTMLSpanElement | null>(null);

  return (
    <div
      className={cx(styles.rowBox, !item.available && styles.inactive)}
      onMouseEnter={isMobile ? () => setHoverOpen(true) : undefined}
      onMouseLeave={isMobile ? () => setHoverOpen(false) : undefined}
    >
      <div className={styles.thumb} aria-hidden>
        <CustomImage
          src={item.imageUrl || 'web_v2/thumb/uh-icon-1000x1000.png/w_64'}
          alt={`${item.label} biomarker panel`}
          fill
        />
      </div>
      <div className={styles.rowText}>
        <span>
          {item.label}
          {item.availableBiomarkers?.length
            ? ` (${item.availableBiomarkers?.length})`
            : null}
        </span>
        <span
          ref={iconRef}
          className={cx(styles.infoIcon, !item.available && styles.hideWeb)}
          aria-hidden
          onMouseEnter={() => setHoverOpen(true)}
          onMouseLeave={() => setHoverOpen(false)}
        >
          <InfoIconSvg />
        </span>
      </div>
      <Popover
        anchorEl={iconRef.current}
        open={hoverOpen}
        onMouseEnter={() => setHoverOpen(true)}
        onMouseLeave={() => setHoverOpen(false)}
      >
        <P style={{ margin: 0 }}>
          {item.availableBiomarkers?.length ? (
            <span>{item.availableBiomarkers.join(', ')}</span>
          ) : null}
          {item.unavailableBiomarkers?.length ? (
            <>
              {item.availableBiomarkers?.length ? ', ' : null}
              <span className={styles.unavailableText}>
                {item.unavailableBiomarkers.join(', ')}
              </span>
            </>
          ) : null}
        </P>
      </Popover>
    </div>
  );
}

const InfoIconSvg = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="13"
    height="12"
    viewBox="0 0 13 12"
    fill="none"
  >
    <path
      d="M6.5 11C9.26142 11 11.5 8.76142 11.5 6C11.5 3.23858 9.26142 1 6.5 1C3.73858 1 1.5 3.23858 1.5 6C1.5 8.76142 3.73858 11 6.5 11Z"
      stroke="black"
      strokeOpacity="0.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.5 8V6"
      stroke="black"
      strokeOpacity="0.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.5 4H6.50667"
      stroke="black"
      strokeOpacity="0.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default BiomarkerTable;
